import {
  type GridView,
  GridViewDialog,
  type GridViewDialogToggle
} from "@appretio/shared";
import { Button, type Option, SingleSelect } from "@appretio/ui-kit";
import { useState } from "react";
import { type SavedViewPickerTexts, VIEW_PICKER_TEXTS } from "./pickerTexts";
import { viewSelectorSlot } from "./SavedViewPicker.css";
import type { ActiveView, GridViewHousekeeping } from "./useGridViewWrites";
import { DeleteViewButton } from "./viewActions/DeleteViewButton";
import { EditViewButton } from "./viewActions/EditViewButton";
import { SetDefaultViewButton } from "./viewActions/SetDefaultViewButton";

const BUILT_IN_PREFIX = "builtin:";
const SAVED_PREFIX = "saved:";

export interface SavedViewPickerProps<K extends string, T extends string> {
  /** The eyebrow over the trigger, naming the list. */
  label: string;
  /** Sizing wrapper around the trigger; defaults to the control bar's fixed slot. */
  slotClassName?: string;
  /** What the trigger reads instead of the label when no view is applied. */
  placeholder?: string;
  /** The words the dialogs use; defaults to the View picker's. */
  texts?: SavedViewPickerTexts;
  /** The code-owned views, listed first in this order, each saying what it sets. */
  builtIns?: readonly { key: K; description: string }[];
  /** The user's own views for this list. */
  views: readonly GridView[];
  /** One line under a saved view's name saying what it sets. */
  describe: (view: GridView) => string;
  /** Which view is applied. */
  activeView: ActiveView<K>;
  /** The applied saved view, which the housekeeping icons act on. */
  activeSavedView?: GridView;
  /** Whether the live state has drifted from the applied view. */
  isDirty: boolean;
  /** Applies a built-in. */
  onPickBuiltIn?: (key: K) => void;
  /** Applies one of the user's views. */
  onPickSaved: (view: GridView) => void;
  /** The writes behind the icons. */
  writes: GridViewHousekeeping;
  /** Save dialog switches, sections first and ``isDefault`` last. */
  saveToggles: readonly GridViewDialogToggle<T>[];
  /** Switch state the save dialog opens with. */
  saveDefaults: Record<T, boolean>;
  /** Keys of which at least one must stay on. */
  requireOneOf?: readonly T[];
  /** Persists the current state under ``name``. */
  onSave: (name: string, toggles: Record<T, boolean>) => Promise<unknown>;
  /** Reports the save dialog opening and closing. */
  onSaveOpenChange?: (open: boolean) => void;
}

/**
 * A picker over one saved-view list: the code-owned entries and the user's
 * own in a single list, with what each one sets spelled out under its name.
 *
 * Housekeeping follows the version selectors: crosshair, pencil and trashcan
 * inside the trigger, acting on the applied view, and a New button in the
 * footer. Re-picking the applied row re-applies it, which is how a drifted
 * view (the dot on the trigger) gets reset.
 */
export function SavedViewPicker<K extends string, T extends string>({
  label,
  slotClassName = viewSelectorSlot,
  placeholder,
  texts = VIEW_PICKER_TEXTS,
  builtIns = [],
  views,
  describe,
  activeView,
  activeSavedView,
  isDirty,
  onPickBuiltIn,
  onPickSaved,
  writes,
  saveToggles,
  saveDefaults,
  requireOneOf,
  onSave,
  onSaveOpenChange
}: SavedViewPickerProps<K, T>) {
  const [saveOpen, setSaveOpenState] = useState(false);

  function setSaveOpen(open: boolean) {
    setSaveOpenState(open);
    onSaveOpenChange?.(open);
  }

  // A built-in's name is taken too: two rows reading "Compact YoY" would be
  // indistinguishable in the list.
  const builtInNames = builtIns.map(({ key }) => key);

  const selectedValue =
    activeView.kind === "builtIn"
      ? `${BUILT_IN_PREFIX}${activeView.key}`
      : activeView.kind === "saved"
        ? `${SAVED_PREFIX}${activeView.viewId}`
        : undefined;

  // The dot marks drift from the applied view. It rides on the option's own
  // label because the trigger reads the label out of the option list.
  function labelFor(value: string, name: string) {
    return value === selectedValue && isDirty ? `${name} ●` : name;
  }

  const options: Option[] = [
    ...builtIns.map(({ key, description }) => ({
      label: labelFor(`${BUILT_IN_PREFIX}${key}`, key),
      value: `${BUILT_IN_PREFIX}${key}`,
      description
    })),
    ...views.map((view) => ({
      label: labelFor(
        `${SAVED_PREFIX}${view.view_id}`,
        view.is_default ? `${view.name} (default)` : view.name
      ),
      value: `${SAVED_PREFIX}${view.view_id}`,
      description: describe(view)
    }))
  ];

  function handleChange(value: string) {
    if (value.startsWith(BUILT_IN_PREFIX)) {
      const key = value.slice(BUILT_IN_PREFIX.length);
      const builtIn = builtIns.find((candidate) => candidate.key === key);
      if (builtIn) onPickBuiltIn?.(builtIn.key);
      return;
    }
    const view = views.find(
      (candidate) => candidate.view_id === value.slice(SAVED_PREFIX.length)
    );
    if (view) onPickSaved(view);
  }

  return (
    <>
      <div className={slotClassName}>
        <SingleSelect
          label={label}
          placeholder={placeholder}
          options={options}
          selectedOption={options.find(
            (option) => option.value === selectedValue
          )}
          searchable={options.length > 8}
          clearable={false}
          onChange={handleChange}
          triggerActions={
            <>
              <SetDefaultViewButton
                view={activeSavedView}
                onSetDefault={writes.setDefaultView}
                isPending={writes.isSettingDefault}
                titles={texts.setDefault}
              />
              <EditViewButton
                view={activeSavedView}
                texts={texts.edit}
                existingNames={[
                  ...builtInNames,
                  ...views
                    .filter((view) => view.view_id !== activeSavedView?.view_id)
                    .map((view) => view.name)
                ]}
                onEdit={writes.editActiveView}
                isPending={writes.isSaving}
              />
              <DeleteViewButton
                view={activeSavedView}
                texts={texts.delete}
                onDelete={writes.deleteView}
                isPending={writes.isDeleting}
              />
            </>
          }
          // Close first: the dialog this opens would otherwise unmount with
          // the popover.
          footerContent={(close) => (
            <Button
              variant="tertiary"
              size="xs"
              onClick={() => {
                close();
                setSaveOpen(true);
              }}
            >
              {texts.newLabel}
            </Button>
          )}
        />
      </div>
      <GridViewDialog
        open={saveOpen}
        onOpenChange={setSaveOpen}
        title={texts.saveTitle}
        namePlaceholder={texts.namePlaceholder}
        existingNames={[...builtInNames, ...views.map((view) => view.name)]}
        toggles={saveToggles}
        initialToggles={saveDefaults}
        requireOneOf={requireOneOf}
        isPending={writes.isSaving}
        onConfirm={onSave}
      />
    </>
  );
}
